import React, { useState, useEffect, useCallback, useRef } from "react";
import { Typography, Spin, Button, Dropdown, message, Select } from "antd";
import { FilterOutlined, SortAscendingOutlined, DownOutlined, CloseOutlined, SearchOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useLocation, useHistory } from "react-router-dom";
import BuySignalCard from "./components/BuySignalCard";
import ViewInsightsDrawer from "./components/ViewInsightsDrawer";
import FiltersDrawer from "./components/FiltersDrawer";
import { fetchPrivateListingsPaginated, getBuySignalsDefaultFilters, saveBuySignalsDefaultFilters, clearBuySignalsDefaultFilters, getPrivateListingsMakes, markListingViewed } from "./services/api";
import { mapListingToSignal } from "./utils/mapListingsToSignals";
import facebookLogo from "../../assets/images/logos-facebook.svg";
import offerUpLogo from "../../assets/images/logo-offerup.svg";
import "../../styles/dashboardCardTokens.css";
import "../../pages/Dashboard/components/recommendedListingsCard.css";
import styles from "./buySignals.module.css";

const { Title, Text } = Typography;

const PER_PAGE_OPTIONS = [10, 20, 50];
const DEFAULT_PER_PAGE = 20;

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price (Low–High)" },
  { value: "price_desc", label: "Price (High–Low)" },
  { value: "mileage_asc", label: "Mileage (Low–High)" },
  { value: "mileage_desc", label: "Mileage (High–Low)" },
  { value: "year_asc", label: "Year (Low–High)" },
  { value: "year_desc", label: "Year (High–Low)" },
  { value: "distance_asc", label: "Distance (Low–High)" },
  { value: "distance_desc", label: "Distance (High–Low)" },
];

/** Map UI sort value to API sort_by and sort_order */
function sortToApi(sortBy) {
  switch (sortBy) {
    case "price_asc":
      return { sort_by: "price", sort_order: "asc" };
    case "price_desc":
      return { sort_by: "price", sort_order: "desc" };
    case "mileage_asc":
      return { sort_by: "mileage", sort_order: "asc" };
    case "mileage_desc":
      return { sort_by: "mileage", sort_order: "desc" };
    case "year_asc":
      return { sort_by: "year", sort_order: "asc" };
    case "year_desc":
      return { sort_by: "year", sort_order: "desc" };
    case "distance_asc":
      return { sort_by: "distance", sort_order: "asc" };
    case "distance_desc":
      return { sort_by: "distance", sort_order: "desc" };
    case "newest":
    case "profit":
    default:
      return { sort_by: "listing_date", sort_order: "desc" };
  }
}

/** Map API sort_by + sort_order back to UI sortBy (for default filters load) */
function apiToSort(sort_by, sort_order) {
  if (!sort_by || !sort_order) return "newest";
  const key = `${sort_by}_${sort_order}`;
  const map = {
    price_asc: "price_asc",
    price_desc: "price_desc",
    mileage_asc: "mileage_asc",
    mileage_desc: "mileage_desc",
    year_asc: "year_asc",
    year_desc: "year_desc",
    distance_asc: "distance_asc",
    distance_desc: "distance_desc",
    listing_date_desc: "newest",
    listing_date_asc: "newest",
  };
  return map[key] ?? "newest";
}

/** Single filter state shape for Buy Signals */
const EMPTY_FILTERS = {
  makes: [],
  yearMin: null,
  yearMax: null,
  priceMin: null,
  priceMax: null,
  mileageMin: null,
  mileageMax: null,
  radius: null,
  listingId: null,
};

function countActiveFilters(f) {
  let n = 0;
  if (f.makes && f.makes.length > 0) n += 1;
  if (f.yearMin != null || f.yearMax != null) n += 1;
  if (f.priceMin != null || f.priceMax != null) n += 1;
  if (f.mileageMin != null || f.mileageMax != null) n += 1;
  if (f.radius != null) n += 1;
  if (f.listingId != null && String(f.listingId).trim() !== "") n += 1;
  return n;
}

function parseSearchParams(search) {
  const params = new URLSearchParams(search);
  const makesStr = params.get("makes");
  const makes = makesStr ? makesStr.split(",").filter(Boolean) : [];
  const yearMin = params.get("year_min");
  const yearMax = params.get("year_max");
  const priceMin = params.get("price_min");
  const priceMax = params.get("price_max");
  const mileageMin = params.get("mileage_min");
  const mileageMax = params.get("mileage_max");
  const radiusParam = params.get("radius");
  const radius = radiusParam != null && radiusParam !== "" ? Number(radiusParam) : null;
  const listingIdParam = params.get("listing_id");
  const listingId = listingIdParam != null && listingIdParam !== "" ? String(listingIdParam).trim() : null;
  const listingSite = params.get("listing_site");
  const listingSiteFilter = listingSite === "Facebook" || listingSite === "OfferUp" || listingSite === "Craigslist" ? listingSite : "all";
  return {
    listingType: params.get("tab") || "all",
    listingSiteFilter,
    sortBy: params.get("sort") || "newest",
    page: Math.max(1, parseInt(params.get("page"), 10) || 1),
    perPage: [10, 20, 50].includes(Number(params.get("per_page"))) ? Number(params.get("per_page")) : DEFAULT_PER_PAGE,
    filters: {
      makes,
      yearMin: yearMin != null && yearMin !== "" ? Number(yearMin) : null,
      yearMax: yearMax != null && yearMax !== "" ? Number(yearMax) : null,
      priceMin: priceMin != null && priceMin !== "" ? Number(priceMin) : null,
      priceMax: priceMax != null && priceMax !== "" ? Number(priceMax) : null,
      mileageMin: mileageMin != null && mileageMin !== "" ? Number(mileageMin) : null,
      mileageMax: mileageMax != null && mileageMax !== "" ? Number(mileageMax) : null,
      radius: radius != null && !Number.isNaN(radius) ? radius : null,
      listingId,
    },
  };
}

function buildSearchParams(state) {
  const p = new URLSearchParams();
  if (state.listingType && state.listingType !== "all") p.set("tab", state.listingType);
  if (state.listingSiteFilter && state.listingSiteFilter !== "all") p.set("listing_site", state.listingSiteFilter);
  if (state.sortBy && state.sortBy !== "newest") p.set("sort", state.sortBy);
  if (state.page > 1) p.set("page", String(state.page));
  if (state.perPage !== DEFAULT_PER_PAGE) p.set("per_page", String(state.perPage));
  const f = state.filters || {};
  if (f.makes && f.makes.length > 0) p.set("makes", f.makes.join(","));
  if (f.yearMin != null) p.set("year_min", String(f.yearMin));
  if (f.yearMax != null) p.set("year_max", String(f.yearMax));
  if (f.priceMin != null) p.set("price_min", String(f.priceMin));
  if (f.priceMax != null) p.set("price_max", String(f.priceMax));
  if (f.mileageMin != null) p.set("mileage_min", String(f.mileageMin));
  if (f.mileageMax != null) p.set("mileage_max", String(f.mileageMax));
  if (f.radius != null) p.set("radius", String(f.radius));
  return p.toString();
}

function formatFetchedAt(listingDate) {
  if (!listingDate) return "—";
  const then = new Date(listingDate);
  if (isNaN(then.getTime())) return "—";
  const now = new Date();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
}

export default function BuySignals() {
  const location = useLocation();
  const history = useHistory();

  const [buySignals, setBuySignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSignal, setDrawerSignal] = useState(null);
  const [sortBy, setSortBy] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [lastScan, setLastScan] = useState("—");
  // Pagination & API params
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [listingType, setListingType] = useState("all");
  const [listingSiteFilter, setListingSiteFilter] = useState("all");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [defaultFilters, setDefaultFilters] = useState(null);
  const [recommendedCount, setRecommendedCount] = useState(null);
  const [privateOnlyCount, setPrivateOnlyCount] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [urlParamsApplied, setUrlParamsApplied] = useState(false);
  const [makesOptions, setMakesOptions] = useState([]);
  const [hideViewed, setHideViewed] = useState(false);

  useEffect(() => {
    getPrivateListingsMakes()
      .then((list) => setMakesOptions(Array.isArray(list) ? list : []))
      .catch(() => setMakesOptions([]));
  }, []);

  const { sort_by, sort_order } = sortToApi(sortBy);
  const activeFilterCount = countActiveFilters(filters);
  const sortLabel = SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? "Newest";

  const loadListings = useCallback(() => {
    setLoading(true);
    setError(null);
    const mileageMinNum = filters.mileageMin != null ? Number(filters.mileageMin) : undefined;
    const mileageMaxNum = filters.mileageMax != null ? Number(filters.mileageMax) : undefined;
    fetchPrivateListingsPaginated({
      page,
      per_page: perPage,
      listing_type: listingType,
      listing_site: listingSiteFilter === "all" ? undefined : listingSiteFilter,
      sort_by,
      sort_order,
      makes: filters.makes?.length ? filters.makes : undefined,
      min_year: filters.yearMin ?? undefined,
      max_year: filters.yearMax ?? undefined,
      min_mileage: mileageMinNum,
      max_mileage: mileageMaxNum,
      min_price: filters.priceMin ?? undefined,
      max_price: filters.priceMax ?? undefined,
      radius: filters.radius ?? undefined,
      listing_id: filters.listingId != null && String(filters.listingId).trim() !== "" ? String(filters.listingId).trim() : undefined,
      hide_viewed: hideViewed || undefined,
    })
      .then((data) => {
        const list = data?.listings ?? [];
        const items = Array.isArray(list) ? list : [];
        let signals = items.map(mapListingToSignal).filter(Boolean);
        // Client-side filter for Underpriced tab when API returns all
        if (listingType === "underpriced") {
          signals = signals.filter((s) => s.underpriced === true);
        }
        // Client-side radius filter: keep listings within radius (or with no distance)
        if (filters.radius != null) {
          signals = signals.filter((s) => s.distanceMi == null || s.distanceMi <= filters.radius);
        }
        // Client-side distance sort (when API may not support it)
        if (sortBy === "distance_asc") {
          signals = [...signals].sort((a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity));
        } else if (sortBy === "distance_desc") {
          signals = [...signals].sort((a, b) => (b.distanceMi ?? -1) - (a.distanceMi ?? -1));
        }
        setBuySignals(signals);
        setTotalCount(data?.total_count ?? 0);
        setTotalPages(data?.total_pages ?? 0);
        if (listingType === "recommended") setRecommendedCount(data?.total_count ?? 0);
        if (listingType === "private_only") setPrivateOnlyCount(data?.total_count ?? 0);
        setSelectedSignal(signals.length > 0 ? signals[0] : null);
        const firstDate = items[0]?.listing_date ?? items[0]?.fetched_at;
        setLastScan(formatFetchedAt(firstDate));
      })
      .catch((err) => {
        console.error("Buy Signals: failed to load listings", err);
        setError(err?.response?.data?.message || "Failed to load buy signals. Please try again.");
        setBuySignals([]);
        setTotalCount(0);
        setTotalPages(0);
        setSelectedSignal(null);
      })
      .finally(() => setLoading(false));
  }, [page, perPage, listingType, listingSiteFilter, sort_by, sort_order, filters, hideViewed]);

  // Only fetch after URL params have been applied so ?listing_id= etc. is in state for the first request
  useEffect(() => {
    if (!urlParamsApplied) return;
    loadListings();
  }, [loadListings, urlParamsApplied]);

  // Apply URL params first (or load default filters when no params), then set urlParamsApplied so loadListings runs
  const hasFetchedDefaultsRef = useRef(false);
  useEffect(() => {
    const search = location.search;
    if (search && search.length > 1) {
      const parsed = parseSearchParams(search);
      setListingType(parsed.listingType);
      setListingSiteFilter(parsed.listingSiteFilter);
      setSortBy(parsed.sortBy);
      setPage(parsed.page);
      setPerPage(parsed.perPage);
      setFilters(parsed.filters);
      setUrlParamsApplied(true);
    } else {
      if (!hasFetchedDefaultsRef.current) {
        hasFetchedDefaultsRef.current = true;
        getBuySignalsDefaultFilters()
          .then((data) => {
            const saved = data?.filters;
            if (saved && typeof saved === "object" && Object.keys(saved).length > 0) {
              const makes = Array.isArray(saved.make) ? saved.make : (saved.makes ?? []);
              const priceMin = saved.min_price ?? saved.price_min ?? null;
              const priceMax = saved.max_price ?? saved.price_max ?? null;
              const mileageMin = saved.min_mileage ?? saved.mileage_min ?? null;
              const mileageMax = saved.max_mileage ?? saved.mileage_max ?? null;
              const radius = saved.radius ?? null;
              const norm = normalizeFilterState({
                makes,
                yearMin: saved.min_year ?? null,
                yearMax: saved.max_year ?? null,
                priceMin: priceMin != null ? Number(priceMin) : null,
                priceMax: priceMax != null ? Number(priceMax) : null,
                mileageMin: mileageMin != null ? Number(mileageMin) : null,
                mileageMax: mileageMax != null ? Number(mileageMax) : null,
                radius: radius != null ? Number(radius) : null,
                listingId: saved.listing_id ?? null,
              });
              setDefaultFilters(norm);
              setFilters(norm);
              if (saved.listing_type) setListingType(saved.listing_type);
              if (saved.sort_by && saved.sort_order) setSortBy(apiToSort(saved.sort_by, saved.sort_order));
            }
          })
          .catch(() => {});
      }
      setUrlParamsApplied(true);
    }
  }, [location.search]);

  // Sync state to URL when filters or list options change
  useEffect(() => {
    const q = buildSearchParams({
      listingType,
      listingSiteFilter,
      sortBy,
      page,
      perPage,
      filters,
    });
    const nextSearch = q ? `?${q}` : "";
    if (location.search !== nextSearch) {
      history.replace(`${location.pathname}${nextSearch}`);
    }
  }, [listingType, listingSiteFilter, sortBy, page, perPage, filters]);

  function normalizeFilterState(f) {
    return {
      makes: Array.isArray(f.makes) ? f.makes : [],
      yearMin: f.yearMin != null ? f.yearMin : null,
      yearMax: f.yearMax != null ? f.yearMax : null,
      priceMin: f.priceMin != null ? f.priceMin : null,
      priceMax: f.priceMax != null ? f.priceMax : null,
      mileageMin: f.mileageMin != null ? f.mileageMin : null,
      mileageMax: f.mileageMax != null ? f.mileageMax : null,
      radius: f.radius != null ? f.radius : null,
    };
  }

  const handleSortChange = (value) => {
    setSortBy(value);
    setPage(1);
  };
  const handleClearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const removeFilter = (key, value) => {
    if (key === "makes") setFilters((prev) => ({ ...prev, makes: [] }));
    if (key === "make" && typeof value === "string") setFilters((prev) => ({ ...prev, makes: (prev.makes || []).filter((m) => m !== value) }));
    if (key === "year") setFilters((prev) => ({ ...prev, yearMin: null, yearMax: null }));
    if (key === "radius") setFilters((prev) => ({ ...prev, radius: null }));
    if (key === "listingId") setFilters((prev) => ({ ...prev, listingId: null }));
    if (key === "mileage") setFilters((prev) => ({ ...prev, mileageMin: null, mileageMax: null }));
    if (key === "price") setFilters((prev) => ({ ...prev, priceMin: null, priceMax: null }));
    setPage(1);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(normalizeFilterState(newFilters));
    setPage(1);
  };

  const handleUseDefaultFilters = () => {
    if (defaultFilters) {
      setFilters(defaultFilters);
      setPage(1);
    }
  };

  const handleClearDefaultFilters = () => {
    clearBuySignalsDefaultFilters()
      .then(() => {
        message.success("Default filters cleared");
        window.location.reload();
      })
      .catch((err) => {
        const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "Failed to clear default filters";
        message.error(typeof msg === "string" ? msg : "Failed to clear default filters");
      });
  };

  const handleSaveDefaultFilters = (filtersToSave) => {
    const f = filtersToSave ?? filters;
    const { sort_by, sort_order } = sortToApi(sortBy);
    const body = {};
    if (listingType) body.listing_type = listingType;
    const makes = Array.isArray(f.makes) ? f.makes : [];
    if (makes.length > 0) body.make = makes;
    if (f.yearMin != null) body.min_year = f.yearMin;
    if (f.yearMax != null) body.max_year = f.yearMax;
    if (f.mileageMin != null) body.min_mileage = Number(f.mileageMin);
    if (f.mileageMax != null) body.max_mileage = Number(f.mileageMax);
    if (f.priceMin != null) body.min_price = Number(f.priceMin);
    if (f.priceMax != null) body.max_price = Number(f.priceMax);
    if (f.radius != null) body.radius = Number(f.radius);
    body.sort_by = sort_by;
    body.sort_order = sort_order;

    saveBuySignalsDefaultFilters(body)
      .then(() => {
        const norm = normalizeFilterState(f);
        setDefaultFilters(norm);
        setFilters(norm);
        message.success("Default filters saved");
      })
      .catch((err) => {
        const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? err?.message ?? "Failed to save default filters";
        message.error(typeof msg === "string" ? msg : "Failed to save default filters");
      });
  };

  // Enable scrolling and scroll to top on mount and when route changes
  useEffect(() => {
    // Only run if we're on the buy-signals page
    if (!location.pathname.includes('/buy-signals')) {
      return;
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const contentEl = document.querySelector('.content-ant.main-content');
      if (contentEl) {
        // Add class for CSS-based override (similar to MarketSearchPage)
        contentEl.classList.add('buy-signals-scrollable');
        
        // Force enable vertical scrolling using setProperty with !important
        contentEl.style.setProperty('overflow-y', 'auto', 'important');
        contentEl.style.setProperty('overflow-x', 'hidden', 'important');
        contentEl.style.setProperty('height', 'calc(100vh - 40px)', 'important');
        contentEl.style.setProperty('min-height', '100%', 'important');
        
        // Scroll to top
        contentEl.scrollTo({ top: 0, behavior: 'instant' });
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, 10);

    return () => {
      clearTimeout(timer);
      const contentEl = document.querySelector('.content-ant.main-content');
      if (contentEl) {
        contentEl.classList.remove('buy-signals-scrollable');
        // Don't remove properties in cleanup - let the next page handle its own scrolling
        // This ensures scrolling works when coming back to this page
      }
    };
  }, [location.pathname]);

  const handleSignalClick = (signal) => {
    setSelectedSignal(signal);
  };

  const searchFiltered = searchQuery
    ? buySignals.filter(
        (s) =>
          (s.make && s.make.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (s.model && s.model.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : buySignals;
  const signalsForDisplay = searchFiltered;

  const selectedSignalInView = selectedSignal && signalsForDisplay.some((s) => s.id === selectedSignal.id);
  const effectiveSelectedSignal = selectedSignalInView ? selectedSignal : (signalsForDisplay[0] ?? null);

  const handleViewInsights = (signal) => {
    setDrawerSignal(signal);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setDrawerSignal(null);
  };

  const handleMarkViewed = (signal) => {
    if (!signal || signal.viewedAt) return;
    setBuySignals((prev) =>
      prev.map((s) => s.id === signal.id ? { ...s, viewedAt: new Date().toISOString() } : s)
    );
    markListingViewed(signal.id);
  };

  return (
    <div className={`${styles.pageContainer} buy-signals-page`} style={{ padding: '0 24px 12px', paddingLeft: '24px', position: 'relative', paddingTop: '20px', marginTop: '-60px', overflow: 'visible' }}>
      {/* Buy Signals Title - Same style as Market Search (hidden on mobile) */}
      <div className={styles.titleSection} style={{ marginTop: '4px', marginBottom: '8px', paddingTop: '8px', paddingBottom: '6px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Title 
          level={1} 
          style={{ 
            color: '#ffffff', 
            marginBottom: 0,
            fontWeight: 700,
            fontSize: '20px',
            marginTop: 0,
            marginLeft: '0',
            paddingTop: 0,
            lineHeight: 1.3,
            overflow: 'visible'
          }}
        >
          Buy Signals
        </Title>
        
        {/* Subtitle */}
        <div style={{ marginTop: '4px' }}>
          <Text style={{ 
            fontSize: '13px', 
            color: 'rgba(255, 255, 255, 0.55)',
            lineHeight: 1.4
          }}>
            Curated list of top private-party buys to dominate local market—ranked and scored for profit & speed.
          </Text>
        </div>
      </div>

      {/* Listing site filter row - All, Facebook, OfferUp */}
      <div className={styles.listingSiteRow}>
        <button
          type="button"
          className={`${styles.bsSiteTab} ${listingSiteFilter === "all" ? styles.bsSiteTabActive : ""}`}
          onClick={() => { setListingSiteFilter("all"); setPage(1); }}
        >
          All
        </button>
        <button
          type="button"
          className={`${styles.bsSiteTab} ${listingSiteFilter === "Facebook" ? styles.bsSiteTabActive : ""}`}
          onClick={() => { setListingSiteFilter("Facebook"); setPage(1); }}
        >
          <img src={facebookLogo} alt="" className={styles.bsSiteTabLogo} aria-hidden />
          Facebook
        </button>
        <button
          type="button"
          className={`${styles.bsSiteTab} ${listingSiteFilter === "OfferUp" ? styles.bsSiteTabActive : ""}`}
          onClick={() => { setListingSiteFilter("OfferUp"); setPage(1); }}
        >
          <img src={offerUpLogo} alt="" className={styles.bsSiteTabLogo} aria-hidden />
          OfferUp
        </button>
        <button
          type="button"
          className={`${styles.bsSiteTab} ${listingSiteFilter === "Craigslist" ? styles.bsSiteTabActive : ""}`}
          onClick={() => { setListingSiteFilter("Craigslist"); setPage(1); }}
        >
          Craigslist
        </button>
      </div>

      {/* Tabs + Filter/Sort bar */}
      <div className={styles.tabsFilterSortRow}>
        <div className={styles.tabsRow}>
          {[
            { value: "all", label: "All" },
            { value: "recommended", label: "Recommended" },
            { value: "underpriced", label: "Underpriced" },
            { value: "private_only", label: "Private only" },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={`${styles.bsTab} ${listingType === value ? styles.bsTabActive : ""}`}
              onClick={() => { setListingType(value); setPage(1); }}
            >
              {value === "recommended" && <span style={{ opacity: 0.9 }}>★</span>}
              {value === "underpriced" && <DownOutlined style={{ fontSize: 12, marginRight: 4 }} />}
              {label}
            </button>
          ))}
        </div>
        <div className={styles.filterSortGroup}>
          <button
            type="button"
            className={styles.bsFilterBtn}
            style={hideViewed ? { borderColor: "rgba(139, 92, 246, 0.5)", color: "#a78bfa", background: "rgba(139, 92, 246, 0.12)" } : undefined}
            onClick={() => { setHideViewed((v) => !v); setPage(1); }}
          >
            <EyeInvisibleOutlined />
            Hide viewed
          </button>
          <button
            type="button"
            className={styles.bsFilterBtn}
            onClick={() => setFilterDrawerOpen(true)}
          >
            <FilterOutlined />
            Filter
            {activeFilterCount > 0 && (
              <span className={styles.bsFilterBtnBadge}>{activeFilterCount}</span>
            )}
            <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
          </button>
          <Dropdown
            trigger={["click"]}
            overlayClassName={styles.sortDropdownOverlay}
            menu={{
              items: SORT_OPTIONS.map((opt) => ({
                key: opt.value,
                label: opt.label,
                onClick: () => { setSortBy(opt.value); setPage(1); },
              })),
              style: { minWidth: 200, background: "#1a1d2e", borderRadius: 12 },
            }}
          >
            <button type="button" className={styles.bsSortBtn}>
              <SortAscendingOutlined />
              Sort
              <span className={styles.bsSortValue}>{sortLabel}</span>
              <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* Active Filters: label row + Clear all, then chips */}
      {activeFilterCount > 0 && (
        <div className={styles.bsFilterChipsWrap}>
          <div className={styles.bsFilterChipsHeader}>
            <span className={styles.bsFilterChipsLabel}>Active Filters:</span>
            <button type="button" className={styles.bsClearAll} onClick={handleClearAllFilters}>Clear all</button>
          </div>
          <div className={styles.bsFilterChipsRow}>
            {(filters.makes || []).map((m) => (
              <span key={m} className={styles.bsFilterChip}>
                {m}
                <button type="button" className={styles.bsFilterChipRemove} aria-label={`Remove ${m}`} onClick={() => removeFilter("make", m)}><CloseOutlined /></button>
              </span>
            ))}
            {(filters.yearMin != null || filters.yearMax != null) && (
              <span className={styles.bsFilterChip}>
                {filters.yearMin != null && filters.yearMax == null
                  ? `-${filters.yearMin}+`
                  : filters.yearMin == null && filters.yearMax != null
                    ? `Up to ${filters.yearMax}`
                    : `${filters.yearMin ?? "…"}–${filters.yearMax ?? "…"}`}
                <button type="button" className={styles.bsFilterChipRemove} aria-label="Remove year" onClick={() => removeFilter("year")}><CloseOutlined /></button>
              </span>
            )}
            {(filters.mileageMin != null || filters.mileageMax != null) && (
              <span className={styles.bsFilterChip}>
                {filters.mileageMin != null && filters.mileageMax == null
                  ? `>${Number(filters.mileageMin).toLocaleString()} mi`
                  : filters.mileageMin == null && filters.mileageMax != null
                    ? `<${Number(filters.mileageMax).toLocaleString()} mi`
                    : `${Number(filters.mileageMin).toLocaleString()}–${Number(filters.mileageMax).toLocaleString()} mi`}
                <button type="button" className={styles.bsFilterChipRemove} aria-label="Remove mileage" onClick={() => removeFilter("mileage")}><CloseOutlined /></button>
              </span>
            )}
            {(filters.priceMin != null || filters.priceMax != null) && (
              <span className={styles.bsFilterChip}>
                {filters.priceMin != null && filters.priceMax == null
                  ? `>$${Number(filters.priceMin).toLocaleString()}`
                  : filters.priceMin == null && filters.priceMax != null
                    ? `<$${Number(filters.priceMax).toLocaleString()}`
                    : `$${Number(filters.priceMin).toLocaleString()}–$${Number(filters.priceMax).toLocaleString()}`}
                <button type="button" className={styles.bsFilterChipRemove} aria-label="Remove price" onClick={() => removeFilter("price")}><CloseOutlined /></button>
              </span>
            )}
            {filters.radius != null && (
              <span className={styles.bsFilterChip}>
                Within {filters.radius} mi
                <button type="button" className={styles.bsFilterChipRemove} aria-label="Remove radius" onClick={() => removeFilter("radius")}><CloseOutlined /></button>
              </span>
            )}
            {filters.listingId != null && String(filters.listingId).trim() !== "" && (
              <span className={styles.bsFilterChip}>
                Listing #{filters.listingId}
                <button type="button" className={styles.bsFilterChipRemove} aria-label="Remove listing ID" onClick={() => removeFilter("listingId")}><CloseOutlined /></button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main Content - Grid Layout */}
      <div className={styles.mainContent}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
            <Spin size="large" />
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "48px 16px", color: "rgba(255,255,255,0.7)" }}>
            <p style={{ marginBottom: 16 }}>{error}</p>
            <Button type="primary" onClick={loadListings}>Try again</Button>
          </div>
        ) : buySignals.length === 0 ? (
          <div className={styles.noListingsEmpty}>
            <div className={styles.noListingsEmptyIconWrap}>
              <span className={styles.noListingsEmptyIconCircle} />
              <SearchOutlined className={styles.noListingsEmptyIconSearch} />
            </div>
            <h4 className={styles.noListingsEmptyTitle}>No matches found</h4>
            <p className={styles.noListingsEmptyDesc}>Try adjusting your filters to see matching cars.</p>
            <button type="button" className={styles.noListingsEmptyBtn} onClick={() => setFilterDrawerOpen(true)}>
              Adjust Filters
            </button>
          </div>
        ) : signalsForDisplay.length === 0 ? (
          <div className={styles.noListingsEmpty}>
            <div className={styles.noListingsEmptyIconWrap}>
              <span className={styles.noListingsEmptyIconCircle} />
              <SearchOutlined className={styles.noListingsEmptyIconSearch} />
            </div>
            <h4 className={styles.noListingsEmptyTitle}>No {listingSiteFilter !== "all" ? listingSiteFilter + " " : ""}listings on this page</h4>
            <p className={styles.noListingsEmptyDesc}>Try another site filter or adjust your filters.</p>
            <button type="button" className={styles.noListingsEmptyBtn} onClick={() => setListingSiteFilter("all")}>
              Show All
            </button>
          </div>
        ) : (
          <>
            <div className={styles.gridContainer}>
            {signalsForDisplay.map((signal) => (
              <BuySignalCard
                key={signal.id}
                signal={signal}
                isSelected={effectiveSelectedSignal?.id === signal.id}
                onClick={() => handleSignalClick(signal)}
                onViewInsights={() => handleViewInsights(signal)}
                onMarkViewed={() => handleMarkViewed(signal)}
              />
            ))}
            </div>
            {buySignals.length > 0 && (
              <div className={styles.paginationBar}>
                <div className={styles.paginationControls}>
                  <Button
                    type="default"
                    size="small"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={styles.paginationBtn}
                  >
                    Previous
                  </Button>
                  <Text className={styles.paginationText}>
                    Page {page} of {totalPages || 1}
                    {totalCount > 0 && ` (${totalCount} total)`}
                  </Text>
                  <Button
                    type="default"
                    size="small"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={styles.paginationBtn}
                  >
                    Next
                  </Button>
                </div>
                <div className={styles.perPageWrap}>
                  <Text className={styles.perPageLabel}>Results per page</Text>
                  <Select
                    value={perPage}
                    onChange={(v) => { setPerPage(v); setPage(1); }}
                    options={PER_PAGE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
                    className={styles.perPageSelect}
                    size="small"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Filters Drawer */}
      <FiltersDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        initialFilters={filters}
        defaultFilters={defaultFilters}
        makesOptions={makesOptions}
        onApply={handleApplyFilters}
        onResetToDefault={handleUseDefaultFilters}
        onClearDefault={handleClearDefaultFilters}
        onSaveDefault={handleSaveDefaultFilters}
        matchCount={totalCount}
      />

      {/* View Insights Drawer */}
      <ViewInsightsDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        signal={drawerSignal}
      />
    </div>
  );
}
